import React, { useEffect, useCallback } from 'react'

function getAvatarUrl(url) {
  if (!url) return null
  if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=200,height=200,fit=cover,quality=85,format=auto/')
  }
  return url
}

export default function ReferenceModal({
  references = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onSelectIndex,
}) {
  const currentRef = references[currentIndex]

  const handlePrev = useCallback(() => {
    if (!references.length) return
    const nextIdx = currentIndex === 0 ? references.length - 1 : currentIndex - 1
    onSelectIndex(nextIdx)
  }, [currentIndex, references.length, onSelectIndex])

  const handleNext = useCallback(() => {
    if (!references.length) return
    const nextIdx = currentIndex === references.length - 1 ? 0 : currentIndex + 1
    onSelectIndex(nextIdx)
  }, [currentIndex, references.length, onSelectIndex])

  // Keyboard navigation & Esc to close
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        handlePrev()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    // Prevent body scrolling while modal is open
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen, onClose, handlePrev, handleNext])

  if (!isOpen || !currentRef) return null

  const avatar = Array.isArray(currentRef.billede) ? currentRef.billede[0] : currentRef.billede

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reference-modal-title"
    >
      {/* Modal Container */}
      <div
        className="relative w-full max-w-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/80">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold bg-slate-200 text-slate-800">
              Anmeldelse {currentIndex + 1} af {references.length}
            </span>
          </div>

          <button
            onClick={onClose}
            aria-label="Luk modal"
            className="w-8 h-8 rounded-full text-slate-500 hover:text-black hover:bg-slate-200 flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-xmark text-base" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 sm:p-8 overflow-y-auto space-y-6">
          {/* Star rating */}
          <div className="flex items-center gap-1 text-black">
            {[...Array(5)].map((_, i) => (
              <i key={i} className="fa-solid fa-star text-sm text-black" aria-hidden="true" />
            ))}
          </div>

          {/* Full review quote */}
          <div className="relative">
            <i className="fa-solid fa-quote-left text-slate-200 text-4xl absolute -top-4 -left-2 -z-10 select-none pointer-events-none" aria-hidden="true" />
            <p className="text-black text-base sm:text-lg italic leading-relaxed whitespace-pre-line font-normal">
              "{currentRef.anbefaling}"
            </p>
          </div>

          {/* Author info card */}
          <div className="flex items-start gap-4 pt-6 border-t border-slate-200 bg-slate-50 -mx-6 -mb-6 sm:-mx-8 sm:-mb-8 p-6 sm:p-8">
            {avatar ? (
              <img
                src={getAvatarUrl(avatar)}
                alt={currentRef.navn}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border border-slate-300 shrink-0 bg-slate-100"
              />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-slate-200 border border-slate-300 shrink-0 flex items-center justify-center font-bold text-black text-lg">
                {currentRef.navn ? currentRef.navn.charAt(0) : 'R'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 id="reference-modal-title" className="font-bold text-black text-base sm:text-lg leading-snug">
                {currentRef.navn}
              </h3>
              <p className="text-xs sm:text-sm text-slate-800 font-medium mt-0.5">
                {currentRef.titel}
                {currentRef.virksomhed ? ` · ${currentRef.virksomhed}` : ''}
              </p>
              {currentRef.relation && (
                <p className="text-xs text-slate-500 mt-1 leading-tight">
                  {currentRef.relation}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Navigation Controls */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
          <button
            onClick={handlePrev}
            disabled={references.length <= 1}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-semibold text-black bg-slate-100 hover:bg-slate-200 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
            <span>Forrige</span>
          </button>

          {/* Quick Dots / Pager */}
          <div className="flex items-center gap-1.5 overflow-x-auto max-w-[200px] px-2 py-1">
            {references.map((_, idx) => (
              <button
                key={idx}
                onClick={() => onSelectIndex(idx)}
                aria-label={`Gå til anmeldelse ${idx + 1}`}
                className={`h-2 transition-all rounded-full cursor-pointer ${
                  idx === currentIndex
                    ? 'w-6 bg-black'
                    : 'w-2 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={references.length <= 1}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-semibold text-black bg-slate-100 hover:bg-slate-200 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <span>Næste</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
}
